import React, { createContext, useContext, useState, useEffect } from 'react';
import { useInitialData } from '@/contexts/InitialDataContext';

interface SiteLinks {
  bookingUrl: string;
  contactEmail: string;
  contactPhone: string;
  practitionerLoginUrl: string;
  enableRegionSelector?: boolean;
  enableLanguageSelector?: boolean;
}

export interface SEOOverride {
  path: string;
  title?: string;
  description?: string;
  canonicalUrl?: string;
  schema?: string;
  ogImage?: string;
}

const defaultLinks: SiteLinks = {
  bookingUrl: 'https://outlook.office.com/book/Schedulea20minuteDiscoverySession@enneainternational.com/?ismsaljsauthenabled',
  contactEmail: 'hello@fivelensenneagram.com',
  contactPhone: '+27 (0) 21 000 0000',
  practitionerLoginUrl: 'https://portal.fivelensenneagram.com',
  enableRegionSelector: true,
  enableLanguageSelector: true,
};

interface SiteSettingsContextType {
  links: SiteLinks;
  seoOverrides: SEOOverride[];
  loading: boolean;
}

const SiteSettingsContext = createContext<SiteSettingsContextType>({
  links: defaultLinks,
  seoOverrides: [],
  loading: true,
});

export const useSiteSettings = () => useContext(SiteSettingsContext);

export const SiteSettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const initialData = useInitialData();
  const initialLinks = initialData?.siteSettings?.links;
  const initialSeoOverrides = initialData?.siteSettings?.seo?.overrides;

  const [links, setLinks] = useState<SiteLinks>(() => {
    if (initialLinks && typeof initialLinks === 'object') {
      return { ...defaultLinks, ...initialLinks };
    }
    return defaultLinks;
  });
  const [seoOverrides, setSeoOverrides] = useState<SEOOverride[]>(() => {
    if (Array.isArray(initialSeoOverrides)) {
      return initialSeoOverrides;
    }
    return [];
  });
  const [loading, setLoading] = useState<boolean>(!initialData?.siteSettings);

  useEffect(() => {
    if (initialData?.siteSettings) {
      setLoading(false);
      return;
    }

    setLoading(true);
    let cancelled = false;

    async function fetchSettings() {
      try {
        const res = await fetch('/api/public/site-settings');
        if (cancelled) return;
        if (res.ok) {
          const data = await res.json();
          if (data.links) {
            setLinks({ ...defaultLinks, ...data.links });
          }
          if (data.seo && Array.isArray(data.seo.overrides)) {
            setSeoOverrides(data.seo.overrides);
          }
        }
      } catch (error) {
        if (!cancelled) {
          console.warn('Error fetching site settings:', error);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchSettings();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <SiteSettingsContext.Provider value={{ links, seoOverrides, loading }}>
      {children}
    </SiteSettingsContext.Provider>
  );
};
